import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';

/**
 * PTR Enterprise Template - A4 Size
 * Standard professional invoice template
 */
export class A4Version4Template extends BasePdfTemplate {
  override templateName = 'a4-version4';
  override templateVersion = '1.0.0';
  override description =
    'New A4 Size invoice template - A4 size standard layout';
  override pageSize: 'A4' = 'A4';
  override previewImage = 'assets/sampleoutput/ptr-enterprise-sample.png';

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    const storename = JSON.parse(localStorage.getItem('storename') || '{}');
    let storeAddress = '';
    if (storename) {
      const addressParts = [
        storename.address,
        storename.address1,
        storename.address2,
        storename.pincode,
      ].filter((part) => part && part.trim());

      storeAddress = addressParts.join(', ');
    }

    const businessName = storename?.name || data.businessName || '';
    const businessAddress = storeAddress || data.businessAddress || '';
    const businessPhone = storename?.mobile || data.businessPhone || '';
    const businessGSTIN = data.businessGSTIN || '';

    const formatDate = this.formatDate.bind(this);

    const items = data.items;
    const itemsPerPage = 20;
    const chunks = [];
    for (let i = 0; i < items.length; i += itemsPerPage) {
      chunks.push(items.slice(i, i + itemsPerPage));
    }

    // Free items summary (qty + value at original price / MRP)
    let totalFreeQty = 0;
    let totalFreeAmount = 0;
    items.forEach((item: any) => {
      if (Number(item.is_free) === 1) {
        totalFreeQty += Number(item.quantity || 0);
        totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
      }
    });
    const hasFreeItems = totalFreeQty > 0;

    const salesTables: any[] = chunks.map((chunk, index) => ({
      //pageBreak: index > 0 ? 'before' : undefined,

      table: {
        headerRows: 1,
        widths: ['auto', 245, 'auto', 50, 25, 120],
        heights: 16,
        body: [
          [
            {
              text: 'S.No',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'Description of Goods',
              style: 'tableHeader',
              alignment: 'left',
              bold: true,
            },
            {
              text: 'HSN CODE',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'Rate',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'Qty',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
            {
              text: 'Amount',
              style: 'tableHeader',
              alignment: 'center',
              bold: true,
            },
          ],
          ...chunk.map((item: any, i: number) => {
            // const taxableAmount = item.total || 0;
            const taxableAmount = item.discaftertax
              ? (item.price * item.quantity)
              : (item.total || 0);
            const taxAmount = item.taxamount || 0;
            const itemAmount = taxableAmount + taxAmount;
            // Unit (Qty column is only 25pt wide, so unit goes in a sub-line under the name)
            const unitName = getItemUnitName(item);
            // Selected mapping (variants / parameters) — joined into one line because this table has fixed row heights
            const mappingLines = getSelectedMappingLines(item);
            const nameSubLines: string[] = [
              ...(unitName ? [`Unit : ${unitName}`] : []),
              ...(mappingLines.length ? [mappingLines.join(', ')] : []),
            ];

            return [
              { text: i + 1 + index * itemsPerPage, alignment: 'center' },
              // { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, alignment: 'left' },
              nameSubLines.length
                ? {
                  stack: [
                    { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, alignment: 'left' },
                    ...nameSubLines.map((line) => ({ text: line, fontSize: 10, alignment: 'left' })),
                  ],
                }
                : { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, alignment: 'left' },
              { text: item.hsncode || '', alignment: 'center' },
              { text: item.price.toFixed(2), alignment: 'right' },
              { text: item.quantity, alignment: 'center' },
              {
                text: Number(item.is_free) === 1 ? '0.00' : itemAmount.toFixed(2),
                alignment: 'right',
              },
            ];
          }),
          // Free items summary row — only on the last chunk, only when free items exist
          ...(hasFreeItems && index === chunks.length - 1
            ? [
              [
                {
                  text: 'Free Items :',
                  colSpan: 4,
                  alignment: 'right',
                  bold: true,
                },
                {},
                {},
                {},
                { text: String(totalFreeQty), alignment: 'center', bold: true },
                {
                  text: totalFreeAmount.toFixed(2),
                  alignment: 'right',
                  bold: true,
                },
              ],
            ]
            : []),
        ],
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => 'black',
        vLineColor: () => 'black',
      },
      margin: index > 0 ? [0, 0, 0, 0] : [0, 0, 0, 0],
    }));

    // Return final document definition
    return {
      pageSize: 'A4',
      pageMargins: [10, 243, 10, 10],
      header: function (currentPage: number, pageCount: number) {
        return {
          stack: [
            {
              alignment: 'justify',
              bold: true,
              fontSize: 9,
              columns: [
                {
                  text: 'TAX INVOICE',
                  alignment: 'center',
                  margin: [0, 9, 0, 0],
                },
              ],
            },
            {
              columns: [
                /*{
                  image: '', // must be base64
                  width: 100,
                  margin: [15, 10, 0, 2],
                },*/
                {
                  stack: [
                    {
                      text: businessName,
                      fontSize: 22,
                      bold: true,
                      alignment: 'left',
                      margin: [30, 30, 0, 0],
                    },
                    {
                      text: businessAddress,
                      alignment: 'center',
                      fontSize: 11,
                      margin: [0, 0, 110, 0],
                    },
                    {
                      text: `Mob: ${businessPhone}`,
                      alignment: 'center',
                      fontSize: 9,
                      bold: true,
                      margin: [0, 0, 110, 0],
                    },
                    {
                      text: `GSTIN:${businessGSTIN}`,
                      alignment: 'center',
                      fontSize: 9,
                      bold: true,
                      margin: [0, 0, 110, 0],
                    },
                  ],
                },
              ],
            },
            // Horizontal line
            {
              canvas: [
                {
                  type: 'line',
                  x1: 10,
                  y1: 0,
                  x2: 585,
                  y2: 0,
                  lineWidth: 0.5,
                },
              ],
              margin: [0, 0, 0, 0],
            },
            {
              alignment: 'justify',
              columns: [
                {
                  stack: [
                    {
                      text: `To: M/S. ${data.customerName}`,
                      bold: true,
                      margin: [13, 0, 0, 0],
                    },
                    {
                      text: `GSTIN: ${'-'}`,
                      bold: true,
                      margin: [13, 0, 0, 0],
                    },
                  ],
                },
                {
                  table: {
                    widths: [116, 155],
                    body: [
                      [{ text: 'Invoice No', bold: true }, data.invoiceNumber],
                      [{ text: 'Transporter', bold: true }, '-'],
                      [
                        { text: 'Date', bold: true },
                        formatDate(
                          (data.invoiceDate || data.invoicedate || '') as
                          | string
                          | Date,
                        ),
                      ],

                      [{ text: 'L/R No.', bold: true }, '-'],
                      [{ text: 'No. of Bundles', bold: true }, '-'],
                      [
                        { text: 'Payment', bold: true },
                        data.paymentMethod || '-',
                      ],
                      [
                        { text: 'Mobile', bold: true },
                        data.customerMobile || '-',
                      ],
                    ],
                  },
                  layout: {
                    hLineWidth: (i: any) => (i === 0 ? 0 : 0.5),
                    vLineWidth: function (i: any, node: any) {
                      const lastRowIndex = node.table.body.length - 1;
                      if (i === node.table.body[0].length) return 0; // remove right border of last row
                      return 0.5;
                    },
                    hLineColor: () => 'black',
                    vLineColor: () => 'black',
                  },
                },
              ],
            },
          ],
        };
      },
      content: [
        //  Sales table(s) with auto page break and header repeat
        ...salesTables,
        {
          alignment: 'justify',
          columns: [
            {
              stack: [
                {
                  table: {
                    widths: [359], // full width
                    body: [
                      [
                        {
                          text: `Rupees:  ${this.numberToWords(
                            this.getGrandTotal(data),
                          ).replace(/\b\w/g, (c) => c.toUpperCase())} Only`,
                          bold: true,
                          alignment: 'left',
                          margin: [0, 0, 0, 1],
                        },
                      ],
                    ],
                  },
                  layout: {
                    hLineWidth: function (i: any, node: any) {
                      // bottom border only
                      return i === 1 ? 0.5 : 0; // 1 = bottom line
                    },
                    vLineWidth: function (i: any, node: any) {
                      return 0; // no vertical borders
                    },
                    hLineColor: function (i: any, node: any) {
                      return 'black';
                    },
                  },
                },
                {
                  stack: [
                    {
                      text: storename?.name || data.businessName || '',
                      bold: true,
                      fontSize: 12,
                      alignment: 'center',
                      margin: [0, 0, 0, 8],
                    },
                    {
                      table: {
                        widths: ['*'],
                        body: [
                          [
                            {
                              table: {
                                widths: ['*', '*'],
                                body: [
                                  [
                                    {
                                      text: 'A/c No.',
                                      border: [false, false, false, false],
                                    },
                                    {
                                      text: `:`,
                                      border: [false, false, false, false],
                                    },
                                  ],
                                  [
                                    {
                                      text: 'IFSC Code',
                                      border: [false, false, false, false],
                                    },
                                    {
                                      text: `:`,
                                      border: [false, false, false, false],
                                    },
                                  ],
                                  [
                                    {
                                      text: 'Bank',
                                      border: [false, false, false, false],
                                    },
                                    {
                                      text: `:`,
                                      border: [false, false, false, false],
                                    },
                                  ],
                                  [
                                    {
                                      text: 'Branch',
                                      border: [false, false, false, false],
                                    },
                                    {
                                      text: `:`,
                                      border: [false, false, false, false],
                                    },
                                  ],
                                ],
                              },
                              layout: {
                                hLineWidth: function (i: any) {
                                  if (i === 0) return 0.5; // top border
                                  return 0;
                                },
                                vLineWidth: function () {
                                  return 0;
                                },
                                hLineColor: function () {
                                  return '#000';
                                },
                              },
                              margin: [0, 0, 0, 0],
                            },
                          ],
                        ],
                      },
                      layout: {
                        hLineWidth: function (i: any) {
                          if (i === 1) return 0.5; // bottom border on outer table
                          return 0;
                        },
                        vLineWidth: function () {
                          return 0;
                        },
                        hLineColor: function () {
                          return '#000';
                        },
                      },
                      margin: [0, 0, 0, 0],
                    },
                  ],
                },
              ],
            },
            {
              table: {
                widths: [37, 23.7, 120],
                body: [
                  [
                    {
                      text: 'TOTAL',
                      border: [true, false, false, false],
                      alignment: 'right',
                      bold: true,
                    },
                    {
                      text: `${this.getTotalQuantity(data)} `,
                      alignment: 'right',
                      border: [true, false, false, false],
                    },
                    {
                      text: `${this.getFinalTotal(data).toFixed(2)} `,
                      alignment: 'right',
                      border: [true, false, true, false],
                    },
                  ],
                  // Tax rows (IGST or CGST+SGST)
                  ...(this.isInterStateTax(data)
                    ? [
                      // Inter-state: Show IGST only
                      [
                        {
                          text: 'IGST',
                          bold: true,
                          alignment: 'right',
                        },
                        { text: ` `, alignment: 'right' },
                        {
                          text: `${(data.igst || 0).toFixed(2)}`,
                          alignment: 'right',
                          border: [true, true, true, false],
                        },
                      ],
                    ]
                    : [
                      // Intra-state: Show CGST + SGST
                      [
                        { text: 'CGST', alignment: 'right', bold: true },
                        { text: ` `, alignment: 'right' },
                        {
                          text: `${(data.cgst || 0).toFixed(2)} `,
                          alignment: 'right',
                          border: [true, true, true, false],
                        },
                      ],
                      [
                        { text: 'SGST', alignment: 'right', bold: true },
                        { text: ` `, alignment: 'right' },
                        {
                          text: `${(data.sgst || 0).toFixed(2)} `,
                          alignment: 'right',
                          border: [true, true, true, false],
                        },
                      ],
                    ]),
                  // Item Discount row (if present)
                  ...(data.itemDiscount && data.itemDiscount > 0
                    ? [
                      [
                        {
                          text: 'Item Discount',
                          bold: true,
                          alignment: 'right',
                        },
                        { text: ` `, alignment: 'right' },
                        {
                          text: `${data.itemDiscount.toFixed(2)}`,
                          alignment: 'right',
                          border: [true, false, true, false],
                        },
                      ],
                    ]
                    : []),
                  // Fallback to single discount if separate discounts not available
                  ...(!data.itemDiscount &&
                    !data.overallDiscount &&
                    data.discount &&
                    data.discount > 0
                    ? [
                      [
                        {
                          text: 'Discount',
                          bold: true,
                          alignment: 'right',
                        },
                        { text: ` `, alignment: 'right' },
                        {
                          text: `${data.discount.toFixed(2)}`,
                          alignment: 'right',
                          border: [true, false, true, false],
                        },
                      ],
                    ]
                    : []),
                  // TOTAL row - Grand Total before overall discount
                  [
                    {
                      text: 'TOTAL',
                      colSpan: 2,
                      alignment: 'right',
                      fontSize: 14,
                      bold: true,
                    },
                    {},
                    {
                      text: `${(data.totalAmount || data.grandTotal || 0).toFixed(2)} `,
                      alignment: 'right',
                      fontSize: 14,
                      border: [true, true, true, false],
                      bold: true,
                    },
                  ],
                  // Overall Discount row (if present)
                  ...(data.overallDiscount && data.overallDiscount > 0
                    ? [
                      [
                        {
                          text: 'Overall Discount',
                          bold: true,
                          alignment: 'right',
                        },
                        { text: ` `, alignment: 'right' },
                        {
                          text: `- ${data.overallDiscount.toFixed(2)}`,
                          alignment: 'right',
                          border: [true, false, true, false],
                        },
                      ],
                    ]
                    : []),
                  // Loyalty Discount row (if present)
                  ...((Number(data.loyaltyAmount) || 0) > 0
                    ? [
                      [
                        {
                          text: `Loyalty Discount (${Number(data.loyaltyPoints) || 0} pts)`,
                          bold: true,
                          alignment: 'right',
                        },
                        { text: ` `, alignment: 'right' },
                        {
                          text: `- ${(Number(data.loyaltyAmount) || 0).toFixed(2)}`,
                          alignment: 'right',
                          border: [true, false, true, false],
                        },
                      ],
                    ]
                    : []),
                  // NET AMOUNT row (if there's overall discount or loyalty discount)
                  ...((data.overallDiscount && data.overallDiscount > 0) ||
                    (Number(data.loyaltyAmount) || 0) > 0
                    ? [
                      [
                        {
                          text: 'NET AMOUNT',
                          colSpan: 2,
                          alignment: 'right',
                          fontSize: 14,
                          bold: true,
                        },
                        {},
                        {
                          text: `${((data.totalAmount || data.grandTotal || 0) - (data.overallDiscount || 0) - (Number(data.loyaltyAmount) || 0)).toFixed(2)} `,
                          alignment: 'right',
                          fontSize: 14,
                          border: [true, true, false, true],
                          bold: true,
                        },
                      ],
                    ]
                    : []),
                ],
                heights: function (rowIndex: number) {
                  // Make the last row taller
                  return rowIndex === 5 ? 56.5 : 'auto';
                },
              },
            },
          ],
        },
        {
          columns: [
            {
              text: "Buyer's Signature",
              margin: [4, 0, 0, 15],
            },
            {
              stack: [
                {
                  text: `For ${storename?.name || data.businessName || ''}`,
                  margin: [0, 0, 4, 15],
                  alignment: 'right',
                },
                {
                  text: 'Authorized Signatory',
                  alignment: 'right',
                  margin: [0, 0, 4, 0],
                },
              ],
            },
          ],
        },
      ],

      // Outer border
      background: function () {
        return {
          canvas: [
            {
              type: 'rect',
              x: 10,
              y: 19,
              w: 575,
              h: 812,
              r: 0,
              lineColor: '#000000',
              lineWidth: 0.5,
            },
          ],
        };
      },
    };
  }

  private getTotalQuantity(invoice: InvoiceData): number {
    // return invoice.items.reduce(
    //   (sum: any, product: any) => sum + product.quantity,
    //   0,
    // );
    // return invoice.items.reduce(
    //   (sum: any, product: any) =>
    //     Number(product.is_free) === 1 ? sum : sum + product.quantity,
    //   0,
    // );
    // T.Qty includes free qty; only amounts exclude free items
    return invoice.items.reduce(
      (sum: any, product: any) => sum + product.quantity,
      0,
    );
  }

  // Calculate total CGST
  private getTotalCGSTPer(invoice: InvoiceData): number {
    return invoice.items.reduce(
      (sum: any, product: any) => sum + product.ctax,
      0,
    );
  }
  private getTotalSGSTPer(invoice: InvoiceData): number {
    return invoice.items.reduce(
      (sum: any, product: any) => sum + product.stax,
      0,
    );
  }
  private getTotalCGST(invoice: InvoiceData): number {
    return invoice.items.reduce(
      // (sum: any, product: any) => sum + (product.cgstval || 0),
      (sum: any, product: any) =>
        Number(product.is_free) === 1 ? sum : sum + (product.cgstval || 0),
      0,
    );
  }

  // Calculate total SGST
  private getTotalSGST(invoice: InvoiceData): number {
    return invoice.items.reduce(
      // (sum: any, product: any) => sum + (product.sgstval || 0),
      (sum: any, product: any) =>
        Number(product.is_free) === 1 ? sum : sum + (product.sgstval || 0),
      0,
    );
  }

  private getFinalTotal(invoice: InvoiceData): number {
    // Calculate subtotal (price * quantity for all items)
    // return invoice.items.reduce(
    //   (sum: any, product: any) => sum + product.quantity * product.price,
    //   0,
    // );
    // Free items are excluded from the subtotal
    return invoice.items.reduce(
      (sum: any, product: any) =>
        Number(product.is_free) === 1
          ? sum
          : sum + product.quantity * product.price,
      0,
    );
  }

  // Calculate final amount (grand total - overall discount - loyalty discount)
  private getGrandTotal(invoice: InvoiceData): number {
    // Return the final amount after overall discount and loyalty discount
    return (
      (invoice.totalAmount || invoice.grandTotal || 0) -
      (invoice.overallDiscount || 0) -
      (Number(invoice.loyaltyAmount) || 0)
    );
  }

  private numberToWords(num: number): string {
    if (!isFinite(num)) return '';
    const ones = [
      '',
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
    ];
    const tens = [
      '',
      '',
      'Twenty',
      'Thirty',
      'Forty',
      'Fifty',
      'Sixty',
      'Seventy',
      'Eighty',
      'Ninety',
    ];
    const teens = [
      'Ten',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];

    const convertLessThanThousand = (n: number): string => {
      n = Math.floor(n);
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100)
        return (
          tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
        );
      const h = Math.floor(n / 100);
      const rem = n % 100;
      return (
        ones[h] +
        ' Hundred' +
        (rem !== 0 ? ' ' + convertLessThanThousand(rem) : '')
      );
    };

    // work with positive integer part only
    let n = Math.floor(Math.abs(num));
    if (n === 0) return 'Zero';

    const parts: string[] = [];

    const crore = Math.floor(n / 10000000);
    if (crore) {
      parts.push(convertLessThanThousand(crore) + ' Crore');
      n = n % 10000000;
    }

    const lakh = Math.floor(n / 100000);
    if (lakh) {
      parts.push(convertLessThanThousand(lakh) + ' Lakh');
      n = n % 100000;
    }

    const thousand = Math.floor(n / 1000);
    if (thousand) {
      parts.push(convertLessThanThousand(thousand) + ' Thousand');
      n = n % 1000;
    }

    if (n) {
      parts.push(convertLessThanThousand(n));
    }

    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  // Helper methods for tax calculations
  private getTotalIGST(invoice: InvoiceData): number {
    return (
      invoice.igst ||
      invoice.items.reduce(
        // (sum: any, product: any) =>
        //   sum + (product.igstval || product.igstAmount || 0),
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.igstval || product.igstAmount || 0),
        0,
      )
    );
  }

  private isInterStateTax(invoice: InvoiceData): boolean {
    const igst = this.getTotalIGST(invoice);
    const cgst = this.getTotalCGST(invoice);
    const sgst = this.getTotalSGST(invoice);

    // If IGST > 0 and CGST/SGST are 0, it's inter-state
    return igst > 0 && cgst === 0 && sgst === 0;
  }
}
